// src/pages/ReservationForm.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Stack,
  Card,
  CardContent,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { zhHK } from "@mui/x-date-pickers";
import zhTW from "date-fns/locale/zh-TW";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import Looks3Icon from "@mui/icons-material/Looks3";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckoutDrawer from "./reservationPage/CheckoutDrawer";
import TimeMenu from "./TimeMenu";
import PromotionMenu from "./PromotionMenu";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import paymentApi from "../api/app/payment";

// 點數付款相關 API
import {
  fetchMePopulated,
  pickCoinForStore,
  startAppointmentWithCoin,
  previewPrice,
} from "../api/app/appService";
import getSimulatorReservations from "../api/app/getSimulatorReservations";

// Helper: format Date as local ISO string (no UTC conversion)
// e.g., "2026-01-18T02:00:02" - same as displayed on screen
const toLocalISOString = (date) => {
  if (!date) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

// Parse ISO string as local time (no UTC conversion) - same as RoomList.jsx
const parseRawDateTime = (dateTimeStr) => {
  const match = dateTimeStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):?(\d{2})?/);
  if (match) {
    const [, year, month, day, hour, minute, second = "0"] = match;
    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second)
    );
  }
  return new Date(dateTimeStr);
};

// Error message translation table (English -> Traditional Chinese)
const ERROR_TRANSLATIONS = {
  "The appointment time range overlaps with an existing appointment.": "預約時間與現有預約重疊",
  "Invalid time range": "無效的時間範圍",
  "Start time must be before end time": "開始時間必須早於結束時間",
  "Simulator not available": "包廂目前無法使用",
  "Plan not found": "找不到該方案",
  "Insufficient balance": "餘額不足",
  "Unauthorized": "請先登入",
  "Bad Request": "請求無效",
};

// Helper: translate error message
const translateError = (message) => {
  if (!message) return null;
  return ERROR_TRANSLATIONS[message] || message;
};

function ReservationForm() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { storeId } = useParams();

  const today = new Date();
  const { tables, store } = location.state || {};
  const table = tables?.[0];

  const [selectedStartTime, setSelectedStartTime] = useState(today);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [selectedPriceTableIndex, setSelectedPriceTableIndex] = useState(-1);
  const [total, setTotal] = useState(0);
  const [priceInfo, setPriceInfo] = useState(null); // preview-price API 回傳資料
  const [priceLoading, setPriceLoading] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);

  // 付款方式：'online' | 'points'
  const [paymentMethod, setPaymentMethod] = useState("online");

  // 點數流程狀態與提示
  const [pointPayLoading, setPointPayLoading] = useState(false);
  const [coinBalance, setCoinBalance] = useState(null);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState("success"); // success | error | info

  // 時間衝突檢測狀態
  const [availableSlots, setAvailableSlots] = useState([]);
  const [timeOverlap, setTimeOverlap] = useState(false);

  useEffect(() => {
    if (!table || !store) navigate("/app");
  }, [table, store, navigate]);

  // 計算結束時間
  const recalculateEndTime = useCallback(() => {
    if (selectedPriceTableIndex === -1) {
      setSelectedEndTime(null);
      setTotal(0);
      setPriceInfo(null);
      return null;
    }
    const totalHours =
      table.PriceTable.Hour[selectedPriceTableIndex] +
      table.PriceTable.FreeHour[selectedPriceTableIndex];

    const startTime = new Date(selectedStartTime);
    const hours = Math.floor(totalHours);
    const minutes = (totalHours - hours) * 60;

    startTime.setHours(
      startTime.getHours() + hours,
      startTime.getMinutes() + minutes
    );
    setSelectedEndTime(startTime);
    return startTime;
  }, [selectedStartTime, selectedPriceTableIndex, table]);

  // 呼叫 preview-price API 取得實際金額
  useEffect(() => {
    let alive = true;

    const endTime = recalculateEndTime();
    if (!endTime || selectedPriceTableIndex === -1 || !table?.SimulatorId) {
      return;
    }

    const fetchPrice = async () => {
      setPriceLoading(true);
      try {

        const result = await previewPrice({
          startTime: toLocalISOString(selectedStartTime),
          endTime: toLocalISOString(endTime),
          planId: table.PriceTable.PlanId[selectedPriceTableIndex],
          storeSimulatorId: table.SimulatorId,
          status: "pending",
        });
        if (alive) {
          setPriceInfo(result);
          setTotal(result?.amount ?? 0);
        }
      } catch (err) {
        console.error("preview-price error:", err);
        if (alive) {
          // fallback 到本地計算
          const fallbackPrice =
            table.PriceTable.Price[selectedPriceTableIndex] -
            table.PriceTable.Discount[selectedPriceTableIndex];
          setTotal(Math.max(fallbackPrice, 0));
          setPriceInfo(null);
        }
      } finally {
        if (alive) setPriceLoading(false);
      }
    };

    fetchPrice();

    return () => {
      alive = false;
    };
  }, [selectedStartTime, selectedPriceTableIndex, table, forceUpdate, recalculateEndTime]);

  // 當日期變更時，取得該包廂的可用時段
  useEffect(() => {
    if (!table?.SimulatorId || !selectedStartTime) return;

    let alive = true;
    const fetchReservations = async () => {
      try {
        // 格式化日期為 YYYY-MM-DD (與 RoomList.jsx 一致)
        const d = new Date(selectedStartTime);
        const y = d.getFullYear();
        const m = `${d.getMonth() + 1}`.padStart(2, "0");
        const dd = `${d.getDate()}`.padStart(2, "0");
        const dateStr = `${y}-${m}-${dd}`;

        const data = await getSimulatorReservations(table.SimulatorId, dateStr);
        console.log("table.SimulatorId:", table.SimulatorId);
        console.log("dateStr:", dateStr);
        console.log("getSimulatorReservations data (available slots):", data);
        // API 回傳可用時段的陣列 [{startTime, endTime}, ...]
        if (alive && Array.isArray(data)) {
          setAvailableSlots(data);
        }
      } catch (err) {
        console.error("getSimulatorReservations error:", err);
        if (alive) setAvailableSlots([]);
      }
    };

    fetchReservations();
    return () => { alive = false; };
  }, [table?.SimulatorId, selectedStartTime?.toDateString()]);

  // 檢查選擇的時段是否在可用時段內
  useEffect(() => {
    if (!selectedStartTime || !selectedEndTime || availableSlots.length === 0) {
      // 如果沒有選擇時間，或者沒有可用時段資料，暫時不視為衝突 (或視需求調整)
      // 但如果是因為"還沒選結束時間"，當然不算衝突
      // 如果是"當天完全沒空檔" (availableSlots為空)，那只要選了時間就該擋
      if (selectedStartTime && selectedEndTime && availableSlots.length === 0) {
        setTimeOverlap(true);
      } else {
        setTimeOverlap(false);
      }
      return;
    }

    const startMs = selectedStartTime.getTime();
    const endMs = selectedEndTime.getTime();

    // 檢查我們選擇的時段是否 "完全包含" 在某個可用時段內
    // 可用時段條件： startMs >= availableStart && endMs <= availableEnd
    const isContainedInAvailableSlot = availableSlots.some((slot) => {
      const availableStart = parseRawDateTime(slot.startTime).getTime();
      const availableEnd = parseRawDateTime(slot.endTime).getTime();

      console.log("availableStart:", new Date(availableStart).toLocaleString());
      console.log("availableEnd:", new Date(availableEnd).toLocaleString());
      console.log("startMs:", new Date(startMs).toLocaleString());
      console.log("endMs:", new Date(endMs).toLocaleString());

      return startMs >= availableStart && endMs <= availableEnd;
    });

    if (!isContainedInAvailableSlot) {
      console.log("Time is NOT available. Selected:", selectedStartTime, "-", selectedEndTime);
    }

    setTimeOverlap(!isContainedInAvailableSlot);
  }, [selectedStartTime, selectedEndTime, availableSlots]);


  const handleSelectPriceTableIndex = (index) => {
    setSelectedPriceTableIndex(index);
    setForceUpdate((prev) => prev + 1);

    // 立即計算結束時間並檢查是否可用
    // 注意：這裡如果 availableSlots 是空，也應該視為不可用
    if (index !== -1 && table?.PriceTable) {
      const totalHours =
        table.PriceTable.Hour[index] + table.PriceTable.FreeHour[index];
      const endTime = new Date(selectedStartTime);
      const hours = Math.floor(totalHours);
      const minutes = (totalHours - hours) * 60;
      endTime.setHours(endTime.getHours() + hours, endTime.getMinutes() + minutes);

      const startMs = selectedStartTime.getTime();
      const endMs = endTime.getTime();

      let isAvailable = false;
      if (availableSlots.length > 0) {
        isAvailable = availableSlots.some((slot) => {
          const availableStart = parseRawDateTime(slot.startTime).getTime();
          const availableEnd = parseRawDateTime(slot.endTime).getTime();
          return startMs >= availableStart && endMs <= availableEnd;
        });
      }

      if (!isAvailable) {
        setToastType("warning");
        setToastMsg("此時段無法預約 (不在可用時間內)，請選擇其他時間");
        setToastOpen(true);
      }
    }
  };

  // 進入頁面或切換店家時抓取該店家餘額
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await fetchMePopulated();
        const balance = pickCoinForStore(me, store?.Id ?? storeId);
        if (alive) {
          setCoinBalance(balance ?? 0);
        }
      } catch {
        if (alive) setCoinBalance(null);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  // 金流付款
  const handleOnlinePay = async () => {
    try {
      const startTimeWithBuffer = new Date(selectedStartTime.getTime() - 10 * 60000);
      const endTimeWithBuffer = new Date(selectedEndTime.getTime() + 9.9 * 60000);
      console.log("[paymentApi] startTime:", startTimeWithBuffer, "endTime:", endTimeWithBuffer);
      const responseData = await paymentApi(
        toLocalISOString(startTimeWithBuffer), // 舊邏輯緩衝
        toLocalISOString(endTimeWithBuffer),
        table.PriceTable.PlanId[selectedPriceTableIndex],
        table.SimulatorId,
        `${window.location.origin}/app/store/${storeId}/payment-success`
      );

      if (responseData !== null) {
        // Create a form element dynamically
        const form = document.createElement("form");
        form.method = (responseData.payload.properties.method || "POST").toUpperCase();
        form.action = responseData.payload.properties.url;
        form.style.display = "none";

        // Add form fields
        Object.entries(responseData.payload.data).forEach(([k, v]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = k;
          input.value = v;
          form.appendChild(input);
        });

        // Append to body and submit
        document.body.appendChild(form);
        form.submit();
      }
    } catch (err) {
      console.error(err);
      setToastType("error");
      // Display translated error message from API if available
      const apiMessage = err.response?.data?.message;
      console.log("[paymentApi] error:", apiMessage);
      setToastMsg(translateError(apiMessage) || "金流建立失敗，可能時間已經被占用");
      setToastOpen(true);
    }
  };

  // 點數付款
  const handlePointPay = async () => {
    if (selectedPriceTableIndex === -1 || !selectedEndTime) {
      setToastType("info");
      setToastMsg("請先選擇日期、開始時間與使用時間");
      setToastOpen(true);
      return;
    }
    try {
      setPointPayLoading(true);

      // 重新確認餘額
      const me = await fetchMePopulated();
      const balance = pickCoinForStore(me, store?.Id ?? storeId);
      setCoinBalance(balance ?? 0);

      // 使用 preview-price API 回傳的 amount（已包含所有折扣）
      const need = Number(total) || 0;
      if ((balance ?? 0) < need) {
        setToastType("error");
        setToastMsg(`點數不足，需 ${need}，目前 ${balance ?? 0}`);
        setToastOpen(true);
        // 引導去儲值頁
        setTimeout(() => {
          navigate(`/app/${store?.Id ?? storeId}/${me?.id}/charge`);
        }, 600);
        return;
      }

      console.log("[startAppointmentWithCoin] startTime:", selectedStartTime, "endTime:", selectedEndTime);
      await startAppointmentWithCoin({
        storeStatus: "simulator",
        startTime: toLocalISOString(selectedStartTime),
        endTime: toLocalISOString(selectedEndTime),
        planId: table.PriceTable.PlanId[selectedPriceTableIndex],
        venueId: table.SimulatorId,
        status: "pending",
      });

      setToastType("success");
      setToastMsg("以點數預約成功");
      setToastOpen(true);

      setTimeout(() => navigate(`/app/myOrder`), 800);
    } catch (err) {
      if (err?.code === "UNAUTHORIZED") {
        navigate("/login");
        return;
      }
      console.error(err);
      setToastType("error");
      setToastMsg("點數預約失敗");
      setToastOpen(true);
    } finally {
      setPointPayLoading(false);
    }
  };

  // 單一提交入口：依付款方式分流
  const handleSubmit = () => {
    if (paymentMethod === "online") {
      handleOnlinePay();
      return;
    }
    handlePointPay();
  };

  const main = theme.palette.info.main;
  const canSubmit =
    selectedPriceTableIndex !== -1 &&
    selectedEndTime &&
    total > 0 &&
    !timeOverlap &&
    (paymentMethod === "online" ||
      (paymentMethod === "points" && coinBalance !== null));

  const need = Number(total) || 0;
  const pointInsufficient =
    paymentMethod === "points" &&
    coinBalance !== null &&
    coinBalance < need;

  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100vh",
        background: theme.palette.background.default,
        pb: 10,
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: 56,
            px: 2,
          }}
        >
          <IconButton
            edge="start"
            aria-label="back"
            onClick={() => navigate(-1)}
            sx={{ color: "info.main" }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              fontWeight: 700,
              color: "text.primary",
            }}
          >
            請選擇時段
          </Typography>

          <Box sx={{ width: 40 }} />
        </Toolbar>
      </AppBar>

      <Box
        maxWidth={480}
        mx="auto"
        mt={3}
        sx={{ background: theme.palette.background.default }}
      >
        <Card
          sx={{
            borderRadius: 3,
            mt: 2,
            background: theme.palette.background.default,
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardContent>
            <Stack alignItems="center" gap={1}>
              {/* Step 1 */}
              <Stack alignItems="center" gap={0.5} width="100%">
                <LooksOneIcon fontSize="large" sx={{ color: main }} />
                <Typography fontWeight="bold" fontSize={22}>
                  選擇日期
                </Typography>

                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  localeText={
                    zhHK.components.MuiLocalizationProvider.defaultProps
                      .localeText
                  }
                  adapterLocale={zhTW}
                >
                  <StaticDatePicker
                    views={["month", "day"]}
                    displayStaticWrapperAs="desktop"
                    format="yyyy/MM/dd"
                    minDate={today}
                    maxDate={
                      new Date(
                        today.getFullYear(),
                        today.getMonth() + 1,
                        today.getDate()
                      )
                    }
                    value={selectedStartTime}
                    onChange={(newValue) => setSelectedStartTime(newValue)}
                    sx={{
                      background: theme.palette.background.default,
                      borderRadius: 2,
                      ".MuiTypography-root": { color: theme.palette.text.primary },
                      ".MuiPickersCalendarHeader-label": {
                        color: theme.palette.text.primary,
                      },
                      ".MuiPickersDay-root": {
                        border: 0,
                        color: theme.palette.text.primary,
                      },
                      ".MuiPickersArrowSwitcher-root svg": {
                        color: theme.palette.text.primary,
                      },
                      ".MuiPickersDay-root.Mui-selected": {
                        backgroundColor: main,
                        color: theme.palette.text.selected,
                        border: `2px solid ${theme.palette.common.white}`,
                      },
                      ".MuiPickersDay-root:not(.Mui-selected):hover": {
                        backgroundColor: `${main}14`,
                      },
                      ".MuiPickersYear-yearButton.Mui-selected": {
                        backgroundColor: main,
                        color: theme.palette.text.selected,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Stack>

              {/* Step 2 */}
              <Stack mb={2} alignItems="center" width="100%">
                <LooksTwoIcon fontSize="large" sx={{ color: main }} />
                <Typography fontWeight="bold" fontSize={22}>
                  開始時間
                </Typography>
                <TimeMenu
                  selectedTime={selectedStartTime}
                  setSelectedTime={setSelectedStartTime}
                  isTimeReserved={() => false}
                  color={main}
                />
              </Stack>

              {/* Step 3 */}
              <Stack alignItems="center" width="80%">
                <Looks3Icon fontSize="large" sx={{ color: main }} />
                <Typography fontWeight="bold" fontSize={22}>
                  使用時間
                </Typography>
                <PromotionMenu
                  priceTable={table?.PriceTable}
                  selectedStartTime={selectedStartTime}
                  handleSelectPriceTableIndex={handleSelectPriceTableIndex}
                  isTimeReserved={() => false}
                  pillColor={main}
                />
              </Stack>

              {/* Time overlap warning */}
              {timeOverlap && (
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{
                    width: "90%",
                    mt: 2,
                    borderRadius: 2,
                    fontWeight: 600,
                  }}
                >
                  此時段無法預約 (不在可用時間內)，請選擇其他時間
                </Alert>
              )}

              {/* Payment method */}
              <Divider sx={{ width: "100%", my: 2 }} />
              <Stack alignItems="center" width="100%" gap={1}>
                <Typography fontWeight="bold" fontSize={18}>
                  付款方式
                </Typography>
                <Stack direction="row" gap={1}>
                  <Chip
                    label="線上金流"
                    color={paymentMethod === "online" ? "info" : "default"}
                    variant={paymentMethod === "online" ? "filled" : "outlined"}
                    onClick={() => setPaymentMethod("online")}
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    label="點數付款"
                    color={paymentMethod === "points" ? "info" : "default"}
                    variant={paymentMethod === "points" ? "filled" : "outlined"}
                    onClick={() => setPaymentMethod("points")}
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>

                {paymentMethod === "points" ? (
                  <Stack alignItems="center" gap={0.5}>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: pointInsufficient
                          ? theme.palette.error.main
                          : theme.palette.text.secondary,
                      }}
                    >
                      需要點數：{need}　目前餘額：
                      {coinBalance === null ? "讀取中…" : coinBalance}
                      {pointInsufficient ? "（不足，請先儲值）" : ""}
                    </Typography>
                    {/* {priceInfo?.userTypeDiscountPrice > 0 && (
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.success.main }}
                      >
                        身份優惠折扣：-{Math.round(priceInfo.userTypeDiscountPrice)}
                      </Typography>
                    )} */}
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          </CardContent>

          <Divider sx={{ bgcolor: main, opacity: 0.25 }} />
        </Card>

        {/* 原結帳資訊抽屜（顯示金額與時段摘要） */}
        <CheckoutDrawer
          storeName={store?.Name}
          table={table}
          total={total}
          startTime={selectedStartTime}
          endTime={selectedEndTime}
          priceIndex={selectedPriceTableIndex}
          nextStep={() => { }} // 不用這個按鈕提交，由下方統一提交
          onlinePaymentAvailable={store?.OnlinePaymentAvailable}
          mainColor={main}
        />

        {/* 統一提交按鈕：依付款方式分流 */}
        <Box
          maxWidth={480}
          mx="auto"
          mt={2}
          mb={5}
          display="flex"
          gap={1}
          justifyContent="center"
        >
          <Button
            variant="contained"
            color="info"
            onClick={handleSubmit}
            disabled={
              !canSubmit ||
              (paymentMethod === "points" && (pointPayLoading || pointInsufficient))
            }
            sx={{ minWidth: 220, fontWeight: 800 }}
          >
            {paymentMethod === "online"
              ? "前往線上付款"
              : pointPayLoading
                ? "處理中..."
                : "以點數預約"}
          </Button>

        </Box>
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={2500}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastType}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ReservationForm;
